import React, { useState, useEffect } from "react";
import LoaderStyleOne from '../../components/LoaderStyleOne';
import MUIDataTable from "mui-datatables";
import { Grid, Box, TableCell, Typography, Chip, IconButton, Tooltip } from "@mui/material";
import { getData } from "../../ServerRequest";
import { Link } from "react-router-dom";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

const ProductEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate fetching data from the backend
    async function fetchEnquiries() {
      setLoading(true);
      let resData = await getData("/api/buyer/booking/");
      let bookingData = resData["data"]["bookings"];
      if (bookingData && bookingData.length > 0) {
        setEnquiries(bookingData);
      } else {
        setEnquiries([]);
        if (!bookingData) {
          console.error("Error fetching buyers data:", resData.message);
        }
      }
      setLoading(false);
    }

    fetchEnquiries();
  }, []);

  const formatDateTime = (utcDateTime) => {
    const date = new Date(utcDateTime);

    // Convert to IST (UTC+5:30)
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);

    // Format date and time
    const datePart = istDate.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const timePart = istDate.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    return { date: datePart, time: timePart };
  };


  // Approve/Reject handler
  const handleStatusChange = (bookingId, newStatus) => {
    setEnquiries((prevEnquiries) =>
      prevEnquiries.map((enquiry) =>
        enquiry.ps_book_id === bookingId
          ? { ...enquiry, ps_book_status: newStatus }
          : enquiry
      )
    );
  };
  // Custom sort icon renderer (same as CollectionItems.js)
  const renderSortIcon = (columnMeta, sortOrder) => {
    if (sortOrder && sortOrder.index === columnMeta.index) {
      return sortOrder.direction === "asc" ? (
        <ArrowUpwardIcon style={{ fontSize: 16, marginLeft: 4, color: "#333" }} />
      ) : (
        <ArrowDownwardIcon style={{ fontSize: 16, marginLeft: 4, color: "#333" }} />
      );
    }
    return <ArrowUpwardIcon style={{ fontSize: 16, marginLeft: 4, color: "#ccc" }} />;
  };

  const columns = [
    {
      name: "ps_book_id",
      label: "Booking ID",
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          return (
            <Link
              style={{ color: "#0074cd", textDecoration: "none" }}
              to={`/admin/products/enquiryDetail/?booking_id=${value}`}
            >
              {value}
            </Link>
          );
        },
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <TableCell
            key={columnMeta.index}
            style={{
              fontFamily: "inter",
              fontWeight: 600,
              backgroundColor: "#FAFAFB",
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => handleToggleColumn(columnMeta.index)}
          >
            {columnMeta.label}
            {renderSortIcon(columnMeta, sortOrder)}
          </TableCell>
        ),
        setCellProps: () => ({
          style: { textAlign: "center" },
        }),
      },
    },
    {
      name: "buyer_name",
      label: "Buyer Name",
      options: {
        filter: false,
        sort: false,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <TableCell
            key={columnMeta.index}
            style={{
              fontFamily: "inter",
              fontWeight: 600,
              backgroundColor: "#FAFAFB",
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => handleToggleColumn(columnMeta.index)}
          >
            {columnMeta.label}
            {renderSortIcon(columnMeta, sortOrder)}
          </TableCell>
        ),
        setCellProps: () => ({
          style: { textAlign: "center", fontWeight: 700 },
        }),
      },
    },
    {
      name: "ps_book_codes",
      label: "Product Codes",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <TableCell
            key={columnMeta.index}
            style={{
              fontFamily: "inter",
              fontWeight: 600,
              backgroundColor: "#FAFAFB",
              textAlign: "center",
              cursor: "pointer",
              maxWidth: 220,
              minWidth: 180,
              width: 220,
              whiteSpace: "normal",
            }}
            onClick={() => handleToggleColumn(columnMeta.index)}
          >
            {columnMeta.label}
            {renderSortIcon(columnMeta, sortOrder)}
          </TableCell>
        ),
        setCellProps: () => ({
          style: {
            textAlign: "center",
            maxWidth: 220,
            minWidth: 180,
            width: 220,
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          },
        }),
        customBodyRender: (value) => {
          if (!value) return "";
          const codes = Array.isArray(value) ? value : String(value).split(",");
          return (
            <div
              style={{
                maxWidth: 220,
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                textAlign: "center",
                fontSize: 13,
                lineHeight: 1.4,
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                justifyContent: "center",
              }}
            >
              {codes.map((code, idx) => (
                <Chip
                  key={idx}
                  label={code.trim()}
                  size="small"
                  sx={{
                    fontSize: 13,
                    fontWeight: 500,
                    background: "#F3F4F6",
                    color: "#565E6D",
                    borderRadius: "15px",
                    border: "1px solid #565E6D",
                    m: 0.2,
                  }}
                />
              ))}
            </div>
          );
        },
      },
    },
    {
      name: "ps_book_dt",
      label: "Posted On",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <TableCell
            key={columnMeta.index}
            style={{
              fontFamily: "inter",
              fontWeight: 600,
              backgroundColor: "#FAFAFB",
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => handleToggleColumn(columnMeta.index)}
          >
            {columnMeta.label}
            {renderSortIcon(columnMeta, sortOrder)}
          </TableCell>
        ),
        customBodyRender: (value) => {
          if (!value) return "";
          const { date, time } = formatDateTime(value);
          return (
            <span>
              <strong>Date :</strong> {date} <br />
              <strong>Time :</strong> {time}
            </span>
          );
        },
        setCellProps: () => ({
          style: { textAlign: "center" },
        }),
      },
    },
    {
      name: "ps_book_status",
      label: "Status",
      options: {
        filter: true,
        sort: true,
        customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
          <TableCell
            key={columnMeta.index}
            style={{
              fontFamily: "inter",
              fontWeight: 600,
              backgroundColor: "#FAFAFB",
              textAlign: "center",
              cursor: "pointer",
            }}
            onClick={() => handleToggleColumn(columnMeta.index)}
          >
            {columnMeta.label}
            {renderSortIcon(columnMeta, sortOrder)}
          </TableCell>
        ),
        customBodyRender: (value) => {
          let statusText = "Unknown";
          let color = "red";
          switch (value) {
            case 0:
              statusText = "Pending";
              color = "orange";
              break;
            case 1:
              statusText = "Approved";
              color = "green";
              break;
            case 2:
              statusText = "Rejected";
              color = "red";
              break;
            case 3:
              statusText = "Packed";
              color = "#1976d2";
              break;
            case 4:
              statusText = "Shipped";
              color = "#1976d2";
              break;
            case 5:
              statusText = "Delivered";
              color = "#388e3c";
              break;
            default:
              break;
          }
          return <span style={{ color }}>{statusText}</span>;
        },
        setCellProps: () => ({
          style: { textAlign: "center" },
        }),
      },
    },
    {
      name: "actions",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customHeadRender: (columnMeta) => (
          <TableCell
            key={columnMeta.index}
            style={{
              fontWeight: 600,
              backgroundColor: '#FAFAFB'
            }}
          >
            {columnMeta.label}
          </TableCell>
        ),
        customBodyRender: (value, tableMeta) => {
          const bookingId = tableMeta.rowData[0]; // Get the bookingId from row data

          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Approve">
                <IconButton
                  onClick={() => handleStatusChange(bookingId, 'Accepted')}
                  color="success"
                >
                  <CheckIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject">
                <IconButton
                  onClick={() => handleStatusChange(bookingId, 'Rejected')}
                  color="error"
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
        setCellProps: () => ({
          style: { textAlign: "center" },
        }),
      },
    },
  ];

  const options = {
    sort: true,
    elevation: 0,
    filterType: "checkbox",
    responsive: "standard",
    tableBodyHeight: "calc(100vh - 64px - 48px)",
    tableBodyMaxHeight: "calc(100vh - 64px - 48px)",
    print: false, // Disable print
    download: false, // Disable download
    columns: false, // Disable column visibility
    viewColumns: false, // Disable view columns
    filter: false,
    search: true,
    selectableRows: "none", // Disable row selection
    textLabels: {
      body: {
        noMatch: loading ? "Loading..." : "Sorry, no matching records found",
      },
    },
  };

  // Sort enquiries by posted date descending (latest first)
  const sortedEnquiries = React.useMemo(() => {
    return [...enquiries].sort((a, b) => {
      if (a.ps_book_dt && b.ps_book_dt) {
        return new Date(b.ps_book_dt) - new Date(a.ps_book_dt);
      }
      if (a.ps_book_id && b.ps_book_id) {
        return b.ps_book_id - a.ps_book_id;
      }
      return 0;
    });
  }, [enquiries]);

  return (
    <Box sx={{ width: "95%", paddingLeft: 3, mb: 5 }}>
      <Grid container sx={{ flex: 1, height: "100%" }}>
        <Box sx={{ width: "100%", height: "100%" }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
              <LoaderStyleOne />
            </div>
          ) : (
            <Box sx={{ border: "2px solid #F4F3F6" }}>
              <MUIDataTable
                title={
                  <Typography
                    variant="body1"
                    sx={{ fontFamily: "Inter", fontWeight: 700, fontSize: 16 }}
                  >
                    Product Enquiries :{" "}
                    <span style={{ color: "orange" }}>
                      {" "}
                      {sortedEnquiries.length}{" "}
                    </span>
                  </Typography>
                }
                data={sortedEnquiries}
                columns={columns}
                options={options}
              />
            </Box>
          )}
        </Box>
      </Grid>
    </Box>
  );
};

export default ProductEnquiries;
